import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Loader2, Sparkles, Check, GitBranch, Lightbulb } from "lucide-react";
import { toast } from "sonner";

export default function CoachMethod() {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [proposed, setProposed] = useState(null);
  const [learned, setLearned] = useState([]);

  const load = () => api.get("/coach/method").then((r) => setData(r.data));
  const loadLearned = () => api.get("/coach/learned-suggestions").then((r) => setLearned(r.data)).catch(() => {});
  useEffect(() => { load(); loadLearned(); }, []);

  const acceptLearned = async (id) => {
    const r = await api.post(`/coach/learned-suggestions/${id}/accept`);
    toast.success(`Règle ${r.data.created_rule} créée`);
    loadLearned();
  };
  const dismissLearned = async (id) => {
    await api.post(`/coach/learned-suggestions/${id}/dismiss`);
    loadLearned();
  };

  const parse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    try { const r = await api.post("/coach/method/parse", { text }); setProposed(r.data); }
    catch { toast.error("Erreur"); } finally { setParsing(false); }
  };
  const validate = async () => {
    await api.post("/coach/method/validate", proposed);
    toast.success("Principe ajouté à ta méthode");
    setProposed(null); setText(""); load();
  };

  if (!data) return <CoachLayout><div className="pulse-organic w-3 h-3 rounded-full bg-obsidian mx-auto mt-20" /></CoachLayout>;

  return (
    <CoachLayout>
      <div className="fade-up">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-medium">Ma méthode</h1>
          <span className="font-mono text-xs bg-obsidian text-white px-2 py-1 rounded-sm flex items-center gap-1.5"><GitBranch size={12} /> v{data.version}</span>
        </div>
        <p className="mt-2 text-sm text-obsidian/50">Apprends progressivement au moteur ta manière de programmer. Rien n'entre sans ta validation.</p>

        {learned.length > 0 && (
          <div className="mt-6" data-testid="learned-section">
            <div className="flex items-center gap-2">
              <Lightbulb size={16} strokeWidth={1.6} className="text-[#8C6B16]" />
              <h2 className="text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50">Tendances détectées par l'IA</h2>
            </div>
            <div className="mt-4 grid gap-3">
              {learned.map((s) => (
                <div key={s.suggestion_id} data-testid={`learned-${s.suggestion_id}`} className="bg-white border border-[#F2E4C2] p-5">
                  <ProvenanceBadge type="recommandation" className="mb-3">DÉTECTÉ · {s.occurrences}× </ProvenanceBadge>
                  <p className="text-sm text-obsidian/85">{s.pattern}</p>
                  <div className="mt-3 border border-border p-3 text-sm">
                    <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-obsidian/50 mb-1.5">Règle proposée</div>
                    <div className="font-medium">{s.proposed_rule?.name}</div>
                    <div className="mt-1 text-obsidian/60 text-xs"><span className="font-semibold">SI</span> {s.proposed_rule?.conditions} <span className="font-semibold">→</span> {s.proposed_rule?.action}</div>
                  </div>
                  <p className="mt-3 text-xs text-obsidian/50">Souhaites-tu créer une règle à partir de cette préférence ?</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => dismissLearned(s.suggestion_id)} className="rounded-sm border border-border px-4 py-2 text-sm">Ignorer</button>
                    <button data-testid={`learned-accept-${s.suggestion_id}`} onClick={() => acceptLearned(s.suggestion_id)} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Check size={14} /> Créer la règle</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 bg-white border border-border p-6 max-w-2xl">
          <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-3">Nouveau principe</div>
          <textarea data-testid="method-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="Ex : chez un débutant, je préfère plusieurs petites expositions hebdomadaires plutôt qu'une énorme séance par groupe musculaire."
            className="w-full resize-none border border-border p-3 text-sm rounded-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="method-parse-button" onClick={parse} disabled={parsing || !text.trim()}
            className="mt-3 rounded-sm border border-obsidian px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-obsidian hover:text-white transition-[background-color,color] disabled:opacity-40">
            {parsing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Structurer
          </button>

          {proposed && (
            <div className="mt-5 border border-border p-4 fade-up">
              <ProvenanceBadge type="recommandation" className="mb-3">PROPOSITION</ProvenanceBadge>
              <div className="space-y-1.5 text-sm">
                <Row k="Population" v={proposed.population} />
                <Row k="Domaine" v={proposed.domain} />
                <Row k="Principe" v={proposed.principle} />
                <Row k="Priorité" v={proposed.priority} />
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setProposed(null)} className="rounded-sm border border-border px-4 py-2 text-sm">Refuser</button>
                <button data-testid="method-validate-button" onClick={validate} className="rounded-sm bg-obsidian text-white px-4 py-2 text-sm inline-flex items-center gap-1.5"><Check size={14} /> Valider</button>
              </div>
            </div>
          )}
        </div>

        <h2 className="mt-10 text-lg font-medium">Principes actuels</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {data.principles.map((p, i) => (
            <div key={i} className="bg-white border border-border p-5">
              <div className="flex items-center gap-2 text-[11px]">
                <span className="uppercase tracking-wide bg-secondary px-2 py-0.5 rounded">{p.population}</span>
                <span className="uppercase tracking-wide bg-secondary px-2 py-0.5 rounded">{p.domain}</span>
                <span className="text-[#4A6B53]">✓ {p.status}</span>
              </div>
              <p className="mt-3 text-sm text-obsidian/85">{p.principle}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-10 text-lg font-medium">Versions</h2>
        <div className="mt-3 space-y-2">
          {data.versions.map((v, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <span className="font-mono bg-secondary px-2 py-0.5 rounded-sm">v{v.version}</span>
              <span className="text-obsidian/60">{v.note}</span>
            </div>
          ))}
        </div>
      </div>
    </CoachLayout>
  );
}
function Row({ k, v }) {
  if (!v) return null;
  return (<div className="flex justify-between gap-4"><span className="text-obsidian/50">{k}</span><span className="font-medium text-right">{String(v)}</span></div>);
}
